/**
 * The "−" on today's target — A2's `canRemoveSet` and `withoutPendingSet`,
 * the rules fixed after phone testing.
 *
 * ── A PENDING SET LEAVES; A LOGGED ONE NEVER DOES ──────────────────────────
 * Taking a logged set back is "Desfazer", a different act. So the target never
 * drops below the sets already done, and never below one: an exercise the
 * person has not started cannot be removed set by set down to nothing.
 *
 * ── THE PLAN DOES NOT CHANGE ────────────────────────────────────────────────
 * Only `extra` moves (it goes negative); the summary reads it back as "N
 * série(s) a menos que o plano de hoje, por escolha sua" (`sessionNumbers`'s
 * `removed`).
 *
 * ── COMPLETION IS A SIGNAL, NOT A PHASE ─────────────────────────────────────
 * A2 flipped `phase` to `"done"` here. Ending a session is a server write
 * through the outbox, so this only reports `completed` and the store decides
 * what completion does. The next set's pre-filled numbers are likewise the
 * store's, from `suggestNextSet`, once it has the new cursor.
 */
/** A set of today's target for the current exercise can still come off. */
export function canRemoveSet(state, live) {
    if (state.phase !== "log" && state.phase !== "rest") {
        return false;
    }
    const done = state.logged[state.exIndex]?.length ?? 0;
    return (live.sets[state.exIndex] ?? 0) > Math.max(1, done);
}
/**
 * Today's target for the current exercise, one set lower.
 *
 * While resting, the current exercise is the one the next set belongs to. When
 * that set was its last, the rest carries on towards the next open exercise
 * AFTER it, else the first open one before it, else the session is complete.
 * While logging, the view already says the sets are done and offers the next
 * exercise, so the cursor stays.
 *
 * `live` is the workout BEFORE the removal; the target after it is the same
 * minus one on the current exercise, since the target is `targetSets + extra`.
 */
export function withoutPendingSet(state, live) {
    if (!canRemoveSet(state, live)) {
        return { completed: false, state };
    }
    const i = state.exIndex;
    const next = {
        ...state,
        extra: { ...state.extra, [i]: (state.extra[i] ?? 0) - 1 },
    };
    const sets = live.sets.map((n, j) => (j === i ? n - 1 : n));
    const open = (j) => (next.logged[j]?.length ?? 0) < (sets[j] ?? 0);
    if (state.phase === "log" || open(i)) {
        return { completed: false, state: next };
    }
    const after = sets.findIndex((_, j) => j > i && open(j));
    const to = after >= 0 ? after : sets.findIndex((_, j) => open(j));
    if (to < 0) {
        return { completed: true, state: next };
    }
    return {
        completed: false,
        state: { ...next, exIndex: to, setIndex: next.logged[to]?.length ?? 0 },
    };
}
//# sourceMappingURL=set-removal.js.map